const ADMINISTRATION_MODULE_ID = 'administration-module';
const NAV_MODULES = Object.freeze([
  // Administration is declared first so it renders first in the nav bar for every role that gets
  // it (admin/org_admin/introducer-manager/service-provider-manager/introducer/service-provider) —
  // nav render order follows this array's declaration order, not alphabetical or any per-role list.
  // Keep this entry first if more modules are added above/below it later.
  { id: ADMINISTRATION_MODULE_ID, label: 'Administration', badge: 'AD', kind: 'administration', scopeRequirement: 'none', requiredPermission: 'module.admin-platform.access', roles: Object.freeze(['admin', 'org_admin', 'introducer-manager', 'service-provider-manager', 'introducer', 'service-provider']), canHide: false, canReorder: true },
  { id: 'project-plan-module', label: 'Project Plan Module', badge: 'PP', kind: 'projectPlan', requiredPermission: 'module.project-plan.access' },
  { id: 'customer-questionnaire-module', label: 'Customer Questionnaire Module', badge: 'CQ', kind: 'customerQuestionnaire', requiredPermission: 'module.customer-questionnaire.access' },
  { id: 'business-profile-module', label: 'Business Profile Module', badge: 'BP', kind: 'businessProfile', requiredPermission: 'module.business-profile.access' },
  { id: 'customer-information', label: 'Customer Information Module', badge: 'CI', kind: 'customerInformation', requiredPermission: 'module.customer-information.access' },
  { id: 'discover-module', label: 'Discovery Module', badge: 'DB', kind: 'discovery', requiredPermission: 'module.discovery.access' },
  { id: 'considerations-module', label: 'Considerations Module', badge: 'CO', kind: 'considerations', requiredPermission: 'module.considerations.access' },
  { id: 'modelling-module', label: 'Financial Modelling Module', badge: 'MD', kind: 'flow', requiredPermission: 'module.modelling.access' },
  { id: 'consensus-module', label: 'Consensus Module', badge: 'CS', kind: 'consensus', requiredPermission: 'module.consensus.access' },
  { id: 'review-module', label: 'Review Module', badge: 'RV', kind: 'review', requiredPermission: 'module.review.access' },
  { id: 'repository-module', label: 'Repository Module', badge: 'RM', kind: 'repository', requiredPermission: 'module.repository.access' },
  { id: 'diary-module', label: 'Diary Module', badge: 'DY', kind: 'diary', requiredPermission: 'module.diary.access' },
  { id: 'calendar-module', label: 'Calendar Module', badge: 'CA', kind: 'calendar', requiredPermission: 'module.calendar.access' },
  { id: 'payments-module', scopeRequirement: 'none', label: 'Payments Module', badge: 'PM', kind: 'payments', requiredPermission: 'module.payments.access' },
  { id: 'support-module', label: 'Support Module', badge: 'SP', kind: 'support', requiredPermission: 'module.support.access' },
  { id: 'training-module', label: 'Training Module', badge: 'TR', kind: 'training', scopeRequirement: 'none', requiredPermission: 'module.training.access' },
  { id: 'idp-accounting-control-plane-module', label: 'IDP Control Plane', badge: 'IDP', kind: 'idpAccountingControlPlane', requiredPermission: 'module.idp-control-plane.access' },
  { id: 'referral-module', label: 'Referral Module', badge: 'RF', kind: 'referral', requiredPermission: 'module.referral.access' },
]);

const CUSTOMER_SCOPE_REMOUNT_KINDS = new Set([
  'flow', 'discovery', 'customerInformation', 'businessProfile',
  'considerations', 'review', 'projectPlan', 'repository', 'idpAccountingControlPlane',
]);

const CUSTOMER_ONLY_SCOPE_REMOUNT_KINDS = new Set(['customerQuestionnaire', 'consensus']);
const NAV_MODULE_IDS = Object.freeze(NAV_MODULES.map((module) => module.id));
const DEFAULT_VISIBLE_NAV_MODULE_IDS = Object.freeze([
  ADMINISTRATION_MODULE_ID,
  'project-plan-module',
  'customer-questionnaire-module',
  'business-profile-module',
  'customer-information',
  'discover-module',
  'considerations-module',
  'consensus-module',
  'review-module',
  'repository-module',
  'payments-module',
  'training-module',
]);
const DEFAULT_VISIBLE_NAV_MODULE_ID_SET = new Set(DEFAULT_VISIBLE_NAV_MODULE_IDS);
const DEFAULT_HIDDEN_NAV_MODULE_IDS = Object.freeze(
  NAV_MODULE_IDS.filter((moduleId) => !DEFAULT_VISIBLE_NAV_MODULE_ID_SET.has(moduleId))
);

// Per-role overrides of the default bar. Only the Administration-bearing roles (admin,
// org_admin, introducer-manager, service-provider-manager, introducer, service-provider) are listed;
// every other role falls through to DEFAULT_VISIBLE_NAV_MODULE_IDS, deliberately unchanged —
// nothing asked to alter what a working advisor sees on their next sign-in. All six share the same
// single Administration entry (unified-administration-module Task 16, extended to introducer/
// service-provider by the introducer-service-provider-roles task, and to introducer-manager/
// service-provider-manager by this task): they land on the same module id, and AdministrationModule.js
// itself (Task 15) tells them apart by administration mode, not by a second module id.
const ROLE_DEFAULT_VISIBLE_NAV_MODULE_IDS = Object.freeze({
  admin: Object.freeze([ADMINISTRATION_MODULE_ID, 'training-module']),
  org_admin: Object.freeze([ADMINISTRATION_MODULE_ID, 'training-module']),
  'introducer-manager': Object.freeze([ADMINISTRATION_MODULE_ID, 'repository-module', 'training-module']),
  'service-provider-manager': Object.freeze([ADMINISTRATION_MODULE_ID, 'repository-module', 'training-module']),
  introducer: Object.freeze([ADMINISTRATION_MODULE_ID, 'repository-module', 'training-module']),
  'service-provider': Object.freeze([ADMINISTRATION_MODULE_ID, 'repository-module', 'training-module']),
});

// A platform module is the role's home and is always visible. Administration is deliberately
// orderable alongside the ordinary shortcuts but carries canHide:false so neither the UI nor the
// state owner can hide it.
const PLATFORM_MODULE_IDS = Object.freeze([ADMINISTRATION_MODULE_ID]);

function moduleRequiresFamilyScope(moduleDefinition) {
  return moduleDefinition?.scopeRequirement !== 'none';
}

function isModuleAvailableForWorkspaceScope(moduleDefinition, workspaceScope = {}) {
  return !moduleRequiresFamilyScope(moduleDefinition) || Boolean(String(workspaceScope?.customerId || '').trim());
}

function isPlatformModuleId(moduleId) {
  return PLATFORM_MODULE_IDS.includes(typeof moduleId === 'string' ? moduleId : moduleId?.id);
}

function defaultVisibleModuleIdsForRole(role) {
  return [...(ROLE_DEFAULT_VISIBLE_NAV_MODULE_IDS[String(role || '')] || DEFAULT_VISIBLE_NAV_MODULE_IDS)];
}

// The Mitus mark's target, derived from the role's OWN default-visible list above rather than a
// second role->module table: a role with a per-role override (currently the four
// Administration-bearing roles above) lands on that override's first entry, so a later change to
// that role's defaults moves the landing
// target with it and can never leave the mark pointing at something the role can no longer see. A
// role with no override here falls through to the shared DEFAULT_VISIBLE_NAV_MODULE_IDS list, which
// is not "this role's platform" — it is everyone's fallback — so it has no landing module and the
// mark keeps doing what it has always done: '' means "return to the welcome page".
function landingModuleIdForRole(role) {
  const roleOwnDefaults = ROLE_DEFAULT_VISIBLE_NAV_MODULE_IDS[String(role || '')];
  return roleOwnDefaults ? roleOwnDefaults[0] : '';
}

function defaultHiddenModuleIdsForRole(role) {
  const visible = new Set(defaultVisibleModuleIdsForRole(role));
  return NAV_MODULE_IDS.filter((moduleId) => !visible.has(moduleId));
}

export {
  ADMINISTRATION_MODULE_ID,
  CUSTOMER_ONLY_SCOPE_REMOUNT_KINDS,
  CUSTOMER_SCOPE_REMOUNT_KINDS,
  DEFAULT_HIDDEN_NAV_MODULE_IDS,
  DEFAULT_VISIBLE_NAV_MODULE_IDS,
  defaultHiddenModuleIdsForRole,
  defaultVisibleModuleIdsForRole,
  isPlatformModuleId,
  isModuleAvailableForWorkspaceScope,
  landingModuleIdForRole,
  moduleRequiresFamilyScope,
  NAV_MODULE_IDS,
  PLATFORM_MODULE_IDS,
  NAV_MODULES,
};
